import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2, type LucideIcon } from 'lucide-react'
import { clsx } from 'clsx'

// TailAdmin Button.tsx shape (rounded-lg, text-sm medium) at a compact height — md ~38px, sm
// ~32px — with a 1px border on every variant and no shadow. Must stay in step with the .btn
// classes in index.css (used by hand-written markup). Single source of truth for variant ->
// classes, so a button can never silently ignore its own variant prop.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition disabled:cursor-not-allowed focus-visible:outline-none focus-visible:shadow-focus-ring',
  {
    variants: {
      variant: {
        primary: 'border border-brand-500 bg-brand-500 text-white hover:border-brand-600 hover:bg-brand-600 disabled:border-brand-300 disabled:bg-brand-300',
        secondary: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50',
        // Semantic — never recolored by the brand hue.
        danger: 'border border-error-500 bg-error-500 text-white hover:border-error-600 hover:bg-error-600 disabled:border-error-300 disabled:bg-error-300',
        warning: 'border border-warning-500 bg-warning-500 text-white hover:border-warning-600 hover:bg-warning-600 disabled:border-warning-300 disabled:bg-warning-300',
        success: 'border border-success-500 bg-success-500 text-white hover:border-success-600 hover:bg-success-600 disabled:border-success-300 disabled:bg-success-300',
        ghost: 'border border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50',
      },
      size: {
        sm: 'text-sm px-3 py-1.5',
        md: 'text-sm px-4 py-2',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
)

const ICON_SIZE = { sm: 14, md: 16 } as const

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  icon?: LucideIcon
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, icon: Icon, loading, disabled, children, ...props }, ref) => {
    const iconSize = ICON_SIZE[size ?? 'md']
    return (
      <button ref={ref} className={clsx(buttonVariants({ variant, size }), className)} disabled={disabled || loading} {...props}>
        {loading ? <Loader2 size={iconSize} className="animate-spin" /> : Icon && <Icon size={iconSize} />}
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'
